import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Play, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import CTASection from "@/components/CTASection";
import SEO from "@/components/SEO";

export default function Portfolio() {
  const [items, setItems] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFilter = searchParams.get("category") || "All";
  const [filter, setFilter] = useState(initialFilter);
  const [openVideo, setOpenVideo] = useState(null);

  useEffect(() => {
    api
      .get("/portfolio")
      .then((r) => setItems(r.data))
      .catch(() => {});
  }, []);

  // Keep filter in sync with URL param (e.g. when navigating from service page)
  useEffect(() => {
    const cat = searchParams.get("category");
    if (cat && cat !== filter) setFilter(cat);
    if (!cat && filter !== "All") setFilter("All");
  }, [searchParams, filter]);

  const categories = useMemo(
    () => [
      "All",
      "Meta Advertising",
      "Social Media Management",
      "Content Creation",
      "Google Advertising",
      "Website Design",
      "Branding & Design",
      "Email Marketing",
      "SEO",
      "Google Business Profile",
    ],
    []
  );
  const filtered = filter === "All" ? items : items.filter((i) => i.category === filter);

  const onSelect = (c) => {
    setFilter(c);
    if (c === "All") {
      setSearchParams({}, { replace: true });
    } else {
      setSearchParams({ category: c }, { replace: true });
    }
  };

  return (
    <div data-testid="portfolio-page">
      <SEO title="Portfolio" description="A little scrapbook of the brands we've had the pleasure of building, filming and designing for." />
      <section className="pt-16 pb-16 lg:pt-24">
        <div className="ob-container">
          <h1 className="ob-h1 max-w-5xl">A Little Scrapbook of the Brands We've Touched</h1>
        </div>
      </section>

      <section className="pb-24">
        <div className="ob-container">
          <div className="flex flex-wrap gap-2 mb-12">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => onSelect(c)}
                data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`ob-btn ${
                  filter === c ? "ob-btn-primary" : "ob-btn-ghost"
                } h-10 px-5 text-[14px]`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filtered.map((it) => (
              <PortfolioTile
                key={it.id}
                item={it}
                onOpenVideo={() => setOpenVideo(it)}
              />
            ))}
          </div>
        </div>
      </section>

      <VideoDialog
        item={openVideo}
        onOpenChange={(open) => !open && setOpenVideo(null)}
      />

      <CTASection />
    </div>
  );
}

function PortfolioTile({ item, onOpenVideo }) {
  const isVideo = Boolean(item.video);
  const clickable = isVideo;

  const inner = (
    <div className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-[var(--ob-bg-alt)] mb-4">
      {isVideo ? (
        <video
          src={item.video}
          poster={item.image}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className="w-full h-full object-cover"
        />
      ) : (
        <img
          src={item.image}
          alt={item.title}
          className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
        />
      )}

      {isVideo && (
        <>
          {/* subtle dark hover veil */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors duration-300 pointer-events-none" />
          {/* play affordance */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="h-14 w-14 lg:h-16 lg:w-16 rounded-full bg-white/95 flex items-center justify-center shadow-lg opacity-90 group-hover:opacity-100 group-hover:scale-110 transition-all duration-300">
              <Play size={22} strokeWidth={1.75} className="translate-x-[2px]" style={{ color: "var(--ob-ink)" }} />
            </div>
          </div>
        </>
      )}
    </div>
  );

  const meta = null;

  if (clickable) {
    return (
      <button
        type="button"
        onClick={onOpenVideo}
        aria-label={`Play video: ${item.title}`}
        data-testid={`portfolio-${item.id}`}
        className="group text-left w-full"
      >
        {inner}
        {meta}
      </button>
    );
  }

  return (
    <div className="group" data-testid={`portfolio-${item.id}`}>
      {inner}
      {meta}
    </div>
  );
}

function VideoDialog({ item, onOpenChange }) {
  const videoRef = useRef(null);
  const open = Boolean(item);

  useEffect(() => {
    if (open && videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.muted = false;
      videoRef.current.play().catch(() => {
        // fall back to muted autoplay if the browser blocks unmuted
        if (videoRef.current) {
          videoRef.current.muted = true;
          videoRef.current.play().catch(() => {});
        }
      });
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl w-[95vw] p-0 gap-0 border-0 bg-black !rounded-2xl overflow-hidden [&>button.absolute]:hidden"
        data-testid="video-dialog"
      >
        <DialogTitle className="sr-only">
          {item?.title || "Portfolio video"}
        </DialogTitle>

        <div className="relative">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label="Close video"
            data-testid="video-dialog-close"
            className="absolute top-3 right-3 z-20 h-10 w-10 rounded-full bg-white/95 hover:bg-white flex items-center justify-center transition-colors"
          >
            <X size={18} strokeWidth={1.75} style={{ color: "#111" }} />
          </button>

          {item && (
            <div className="w-full aspect-video bg-black">
              <video
                ref={videoRef}
                src={item.video}
                poster={item.image}
                controls
                playsInline
                className="w-full h-full object-contain bg-black"
              />
            </div>
          )}
        </div>

        {item && (
          <div className="px-6 py-5 bg-white flex items-center justify-between gap-4">
            <div>
              <div className="ob-eyebrow mb-1">{item.category}</div>
              <div
                className="text-[18px] font-light leading-tight"
                style={{ color: "var(--ob-ink)" }}
              >
                {item.title}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
